import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GoogleMapsService } from '../../auxiliares/servicios/google-maps.service';

declare const google: any;

export type StreetViewStatus =
  | 'cargando'
  | 'disponible'
  | 'no-disponible'
  | 'sin-coordenadas';

@Component({
  selector: 'app-street-view',
  standalone: true,
  imports: [MatIconModule, MatProgressSpinnerModule],
  templateUrl: './street-view.component.html',
  styleUrls: ['./street-view.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class StreetViewComponent implements AfterViewInit, OnChanges {
  private googleMapsService = inject(GoogleMapsService);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  @Input() coordinates: [number, number] | null = null;
  @Input() heading = 165;
  @Input() pitch = 0;
  @Input() zoom = 1;
  @Input() radius = 50;

  @ViewChild('panorama', { static: false })
  private panoramaEl?: ElementRef<HTMLElement>;

  public status: StreetViewStatus = 'cargando';

  private viewInited = false;

  async ngAfterViewInit(): Promise<void> {
    this.viewInited = true;
    await this.initIfReady();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.viewInited) {
      return;
    }
    if (changes['coordinates']) {
      await this.initIfReady();
    }
  }

  private async initIfReady(): Promise<void> {
    if (!this.coordinates) {
      this.status = 'sin-coordenadas';
      this.cdr.markForCheck();
      return;
    }

    this.status = 'cargando';
    this.cdr.markForCheck();

    try {
      const loadResult = this.googleMapsService.loadApi();
      if (loadResult instanceof Promise) {
        await loadResult;
      }
    } catch {
      this.status = 'no-disponible';
      this.cdr.markForCheck();
      return;
    }

    this.initStreetView();
  }

  private initStreetView(): void {
    const container = this.panoramaEl?.nativeElement;
    if (!container || !this.coordinates) {
      this.status = 'no-disponible';
      this.cdr.markForCheck();
      return;
    }

    const [lng, lat] = this.coordinates;
    const location = { lat, lng };

    const panorama = new google.maps.StreetViewPanorama(container, {
      pov: { heading: this.heading, pitch: this.pitch },
      zoom: this.zoom,
      visible: false,
      addressControl: false,
    });

    const streetViewService = new google.maps.StreetViewService();

    streetViewService.getPanorama(
      { location, radius: this.radius },
      (data: any, status: any) => {
        this.ngZone.run(() => {
          if (status === google.maps.StreetViewStatus.OK) {
            panorama.setPano(data.location.pano);
            panorama.setPov({ heading: this.heading, pitch: this.pitch });
            panorama.setVisible(true);
            this.status = 'disponible';
          } else {
            this.status = 'no-disponible';
          }
          this.cdr.markForCheck();
        });
      },
    );
  }
}
